import { TokenManager, authenticatedFetch } from "@/supabase/supabaseClient";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export class SupabaseImageService {
  static BUCKET_NAME = "task-files";

  /**
   * Get a signed URL for a single file path
   */
  static async getSignedUrl(
    path: string,
    expiresInSeconds = 3600
  ): Promise<string | null> {
    try {
      // Ensure token is ready before making storage operation
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot get signed URL");
        return null;
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/storage/v1/object/sign/${this.BUCKET_NAME}/${path}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            expiresIn: expiresInSeconds,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error creating signed URL:", error);
        return null;
      }

      const data = await response.json();
      const signedURL = data?.signedURL;

      // If the signed URL is relative, make it absolute with the Supabase URL
      if (signedURL && signedURL.startsWith("/")) {
        // Fix the URL structure - Supabase sometimes returns /object/sign/ instead of /storage/v1/object/sign/
        let correctedURL = signedURL;
        if (signedURL.startsWith("/object/sign/")) {
          correctedURL = `/storage/v1${signedURL}`;
        }

        const fullURL = `${supabaseUrl}${correctedURL}`;
        return fullURL;
      }

      return signedURL || null;
    } catch (error) {
      console.error("Error in getSignedUrl:", error);
      return null;
    }
  }

  /**
   * Get signed URLs for multiple file paths
   */
  static async getSignedUrls(
    paths: string[],
    expiresInSeconds = 3600
  ): Promise<string[]> {
    if (!paths.length) return [];

    try {
      // Ensure token is ready before making storage operation
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot get signed URLs");
        return [];
      }

      // Create signed URLs in parallel
      const signedUrlPromises = paths.map((path) =>
        this.getSignedUrl(path, expiresInSeconds)
      );
      const results = await Promise.all(signedUrlPromises);

      // Filter out null results
      const validUrls = results.filter((url) => url !== null) as string[];
      return validUrls;
    } catch (error) {
      console.error("Error in getSignedUrls:", error);
      return [];
    }
  }

  /**
   * Get public URL for a file (if the bucket allows public access)
   */
  static getPublicUrl(path: string): string {
    return `${supabaseUrl}/storage/v1/object/public/${this.BUCKET_NAME}/${path}`;
  }
}
